import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import type { AutoCcSettings } from '../types';

// The bare, lower-cased address of a chip ("Name <a@b.co>" → "a@b.co").
const addrOf = (v: string) => {
  const s = String(v || '').trim();
  const m = s.match(/<([^>]+)>/);
  return (m ? m[1] : s).trim().toLowerCase();
};

// The rules the server applies too (server/src/routes/auth.ts): a full address, and on the "never"
// list also a whole domain — "@gmail.com" (a bare "gmail.com" is taken as that). null = not valid.
const ADDR_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;
const DOMAIN_RE = /^@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;
export function autoCcEntry(raw: string, domains: boolean): string | null {
  let s = String(raw || '').trim().toLowerCase();
  if (domains && s && !s.includes('@')) s = '@' + s;
  return ADDR_RE.test(s) || (domains && DOMAIN_RE.test(s)) ? s : null;
}

const onSkipList = (addr: string, skip: string[]) => {
  const a = addrOf(addr);
  return !!a && skip.some(s => (s.startsWith('@') ? a.endsWith(s) : a === s));
};

/**
 * Auto CC / BCC (Bugify f41022ce, Calem) for the PandaAI Email composers. The user's own addresses
 * for the From mailbox (sidebar → Auto CC / BCC — each mailbox is set up on its own) join every new
 * email, reply, reply all and forward as ordinary chips — shown, previewed, saved in the draft and
 * sent like any other, and removable. They are kept apart from what the user typed (or the reply
 * brought in), so:
 *  - changing the From mailbox swaps them for that mailbox's, whose "never" list then decides afresh;
 *  - while anyone the email goes to is on the "never for these recipients" list they are left off,
 *    and they come back once that recipient is gone — a chip the user typed is never touched;
 *  - one the user removes stays removed;
 *  - the From mailbox itself and addresses already on the email are left out.
 * `enabled` false (a draft that already carries CC/BCC) adds nothing.
 */
export function useAutoCc({ enabled, fromAccountId, fromEmail, to, cc, bcc }: {
  enabled: boolean;
  fromAccountId: string;
  fromEmail: string;
  to: string[]; cc: string[]; bcc: string[];   // the typed chips, without the auto ones
}) {
  // The lists last loaded and the mailbox they belong to. Only the From mailbox's own are used, so the
  // previous mailbox's chips go the moment From changes rather than when the new lists arrive.
  const [loaded, setLoaded] = useState<{ accountId: string; cfg: AutoCcSettings } | null>(null);
  const [dropped, setDropped] = useState<string[]>([]);
  useEffect(() => {
    if (!enabled || !fromAccountId) return;
    let alive = true;
    api.getAutoCc(fromAccountId)
      .then(c => { if (alive) setLoaded({ accountId: fromAccountId, cfg: c }); })
      .catch(() => { /* no settings, or not one of the user's mailboxes → nothing is added */ });
    return () => { alive = false; };
  }, [enabled, fromAccountId]);

  const cfg = loaded && loaded.accountId === fromAccountId ? loaded.cfg : null;
  const typed = [...to, ...cc, ...bcc];
  const skip = cfg?.skip || [];
  const off = !cfg || typed.some(a => onSkipList(a, skip));
  const taken = new Set([fromEmail, ...typed].map(addrOf).filter(Boolean));
  const pick = (list: string[]): string[] => (off ? [] : list.filter(a => {
    const n = addrOf(a);
    if (!n || taken.has(n) || dropped.includes(n)) return false;
    taken.add(n);
    return true;
  }));
  const autoCc = pick(cfg?.cc || []);
  const autoBcc = pick(cfg?.bcc || []);
  return { autoCc, autoBcc, dropAuto: (v: string) => setDropped(p => [...p, addrOf(v)]) };
}
